/**
 * Supported languages (principle 8: Norwegian is the default, every user-visible string
 * lives in `nb.json`/`en.json`).
 *
 * This is the single source of truth for the locale set. `apps/web` builds its i18next
 * resource map from `SUPPORTED_LOCALES`, `apps/api` validates `users.locale` and
 * `organizations.default_locale` against `localeSchema`, and the seed writes one
 * `terms_versions` row per locale per document kind.
 */
import { z } from 'zod';

export const SUPPORTED_LOCALES = ['nb', 'en'] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

/** Principle 8: Norwegian is the default language, everywhere, without exception. */
export const DEFAULT_LOCALE: Locale = 'nb';

export const localeSchema = z.enum(SUPPORTED_LOCALES).meta({ id: 'Locale' });

/** Narrowing guard for values coming from outside the type system (browser, headers, database). */
export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Picks the best supported locale for a raw language tag (`nb-NO`, `en-GB`, `nn`, …),
 * falling back to Norwegian. Region subtags are ignored: Tvitvi translates languages,
 * not regional variants.
 */
export function resolveLocale(value: string | null | undefined): Locale {
  if (typeof value !== 'string' || value.length === 0) return DEFAULT_LOCALE;
  const base = value.toLowerCase().split(/[-_]/)[0] ?? '';
  // Norwegian has three tags in the wild; all three mean nb.json to us.
  if (base === 'no' || base === 'nn' || base === 'nb') return 'nb';
  return isLocale(base) ? base : DEFAULT_LOCALE;
}
